import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router';

import { useSession } from '../auth/session.js';

function initials(firstName: string, lastName: string): string {
  return `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`.toUpperCase();
}

export function UserMenu(): ReactNode {
  const { user, signOut } = useSession();
  const navigate = useNavigate();

  if (user === null) return null;

  const onSignOut = async (): Promise<void> => {
    await signOut();
    void navigate('/sign-in', { replace: true });
  };

  return (
    <Popover className="contents">
      <PopoverButton
        aria-label="Account menu"
        className="flex items-center gap-2 rounded-field py-1 pr-2 pl-1 text-left transition hover:bg-navy/6 dark:hover:bg-white/10"
      >
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-gold text-[12.5px] font-extrabold text-navy">
          {initials(user.firstName, user.lastName)}
        </span>
        <span className="hidden min-w-0 sm:block">
          <span className="block max-w-[140px] truncate text-[13px] font-semibold text-navy dark:text-dark-ink">
            {user.firstName} {user.lastName}
          </span>
        </span>
      </PopoverButton>

      <PopoverPanel
        anchor="bottom end"
        className="z-50 w-44 overflow-hidden rounded-panel border border-hairline bg-white p-1 shadow-menu [--anchor-gap:8px] dark:border-dark-line dark:bg-dark-panel"
      >
        {({ close }: { readonly close: () => void }) => (
          <>
            <div className="px-3 py-2 sm:hidden">
              <p className="truncate text-[13px] font-semibold text-navy dark:text-dark-ink">
                {user.firstName} {user.lastName}
              </p>
              <p className="truncate text-[12px] text-muted">{user.email}</p>
            </div>

            <NavLink
              to="/account"
              onClick={() => {
                close();
              }}
              className="block rounded-field px-3 py-2 text-[13px] font-semibold text-navy transition hover:bg-mist dark:text-dark-ink dark:hover:bg-white/5"
            >
              Account
            </NavLink>

            <button
              type="button"
              onClick={() => {
                close();
                void onSignOut();
              }}
              className="block w-full rounded-field px-3 py-2 text-left text-[13px] font-semibold text-critical transition hover:bg-critical-soft"
            >
              Sign out
            </button>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
}
